import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { Container } from '../../components'
import { axiosInstance } from '../../helpers'
import { useAppDispatch, useCompany, useSchedule, useUser } from '../../hooks'
import {
    ApartmentVendor,
    EndOfTurnReport,
    IdBoolMap,
    ListVendor,
} from '../../models'
import { ArchivedScheduleBody } from './ArchivedScheduleBody'
import { ArchivedScheduleHeader } from './ArchivedScheduleHeader'
import { getInfrastructure, setWorkSpaceUser } from '../../store'
import {
    RateVendorDialog,
    RateVendorTarget,
    isFreshApartmentVendorRating,
} from '../Workforce/RateVendorDialog'

export const ArchivedSchedule = () => {
    const [loadData, setLoadData] = useState(false)

    const { rootUser } = useUser()
    const dispatch = useAppDispatch()
    const location = useLocation()

    const queryParams = new URLSearchParams(location.search)
    const apartment = queryParams.get('apartment')

    useEffect(() => {
        if (apartment) {
            const apartmentId = parseInt(apartment)
            const newWsUser = rootUser?.workspaces.find(
                (wsUser) => wsUser.active_workspace.id === apartmentId,
            )

            if (newWsUser) {
                dispatch(setWorkSpaceUser(newWsUser))
                dispatch(
                    getInfrastructure({
                        apartmentId: newWsUser.active_workspace.id,
                    }),
                )
            }
        }
        setLoadData(true)
    }, [apartment])

    if (loadData) {
        return <_ArchivedSchedule />
    } else {
        return <div />
    }
}

const _ArchivedSchedule = () => {
    const { id } = useParams<{ id?: string }>()

    const { scheduleDetail } = useSchedule({
        cleanUp: true,
        getScheduleDetail: parseInt(id ?? '-1'),
    })
    const { getApartmentVendorList } = useCompany({})

    const [endOfTurnReportList, setEndOfTurnReportList] = useState<
        EndOfTurnReport[]
    >([])

    const [vendorList, setVendorList] = useState<ListVendor[]>([])
    const [apartmentVendors, setApartmentVendors] = useState<ApartmentVendor[]>(
        [],
    )
    const [aptVendorsReady, setAptVendorsReady] = useState(false)
    const [ratedThisVisit, setRatedThisVisit] = useState(false)
    const [rateVendorsOpen, setRateVendorsOpen] = useState(false)
    const autoOpenedForScheduleId = useRef<number | null>(null)

    useEffect(() => {
        setRatedThisVisit(false)
        setRateVendorsOpen(false)
        setAptVendorsReady(false)
        setApartmentVendors([])
        autoOpenedForScheduleId.current = null
    }, [scheduleDetail?.id])

    useEffect(() => {
        if (scheduleDetail) {
            axiosInstance
                .get(`workorder/end-of-turn-report/`, {
                    params: { schedule: scheduleDetail.id },
                })
                .then((res) => {
                    setEndOfTurnReportList(res.data)
                    const vendorIdMap: IdBoolMap = {}
                    const newVendorList: ListVendor[] = []
                    res.data.forEach((eotReport: EndOfTurnReport) => {
                        if (!(eotReport.vendor.id in vendorIdMap)) {
                            vendorIdMap[eotReport.vendor.id] = true
                            newVendorList.push(eotReport.vendor)
                        }
                    })

                    setVendorList(newVendorList)
                })
        }
    }, [scheduleDetail])

    useEffect(() => {
        if (!scheduleDetail) {
            return
        }
        getApartmentVendorList()
            .then((list) => {
                setApartmentVendors(list)
            })
            .catch(() => {
                setApartmentVendors([])
            })
            .finally(() => {
                setAptVendorsReady(true)
            })
    }, [scheduleDetail?.id])

    const aptByVendorId = useMemo(() => {
        const map: Record<number, ApartmentVendor> = {}
        apartmentVendors.forEach((aptVendor) => {
            map[aptVendor.vendor.id] = aptVendor
        })
        return map
    }, [apartmentVendors])

    const rateVendorTargets: RateVendorTarget[] = useMemo(() => {
        return vendorList
            .map((vendor) => {
                const apt = aptByVendorId[vendor.id]
                return {
                    id: vendor.id,
                    name: vendor.name,
                    professionalism: apt?.professionalism,
                    work_quality: apt?.work_quality,
                }
            })
            .sort((a, b) => a.name.localeCompare(b.name))
    }, [vendorList, aptByVendorId])

    const allRatingsFresh =
        aptVendorsReady &&
        vendorList.length > 0 &&
        vendorList.every((vendor) =>
            isFreshApartmentVendorRating(aptByVendorId[vendor.id]),
        )

    const analyticsUnlocked = ratedThisVisit || allRatingsFresh

    useEffect(() => {
        if (!scheduleDetail || !aptVendorsReady || vendorList.length === 0) {
            return
        }
        if (allRatingsFresh) {
            return
        }
        if (autoOpenedForScheduleId.current === scheduleDetail.id) {
            return
        }
        autoOpenedForScheduleId.current = scheduleDetail.id
        setRateVendorsOpen(true)
    }, [scheduleDetail, aptVendorsReady, vendorList.length, allRatingsFresh])

    const openRateVendors = () => {
        setRateVendorsOpen(true)
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                flex: 1,
                overflowX: 'auto',
            }}
        >
            <ArchivedScheduleHeader
                scheduleDetail={scheduleDetail}
                endOfTurnReportList={endOfTurnReportList}
                id={id}
                showRateVendors={vendorList.length > 0}
                onClickRateVendors={openRateVendors}
            />

            <ArchivedScheduleBody
                vendorList={vendorList}
                endOfTurnReportList={endOfTurnReportList}
                analyticsUnlocked={analyticsUnlocked}
                onClickRateVendors={openRateVendors}
            />

            <RateVendorDialog
                open={rateVendorsOpen}
                onClose={() => setRateVendorsOpen(false)}
                title="Rate vendors for this project"
                vendors={rateVendorTargets}
                scheduleId={scheduleDetail?.id}
                onSaved={() => {
                    setRatedThisVisit(true)
                    getApartmentVendorList()
                        .then((list) => {
                            setApartmentVendors(list)
                        })
                        .catch(() => {
                            setApartmentVendors([])
                        })
                }}
            />
        </Container>
    )
}
